import React, { useState, useEffect, useRef } from 'react'
import { useNavigate, useParams, Link } from 'react-router-dom'
import { Container, Row, Col, Card, Button, Spinner, Alert, Form, Table, Badge, OverlayTrigger, Tooltip } from 'react-bootstrap'
import { productionAPI } from '../api/productionAPI'
import { recipeAPI } from '../api/recipeAPI'
import { warehouseAPI } from '../api/warehouseAPI'
import { locationAPI } from '../api/locationAPI'
import { formatErrorResponse } from '../utils/errorHandlers'
import { weightToCases, formatUnitCost } from '../utils/commonUtils'
import { usePermissions } from '../contexts/PermissionContext'
import CopyableText from '../components/CopyableText'
import StockItemInfo from '../components/StockItemInfo'
import QuantityPicker from '../components/QuantityPicker'
import QuantityDisplay from '../components/QuantityDisplay'
import Select from 'react-select'
import { reasonOptions } from '../utils/disposeUtils'
import '../styles/selectControl.css'
import '../styles/productionFinalize.css'

function ProductionFinalizeScreen() {
    const { id } = useParams()
    const navigate = useNavigate()
    const { hasPermission, loading: permissionsLoading } = usePermissions()

    const [productionLot, setProductionLot] = useState(null)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(null)
    const [finalizing, setFinalizing] = useState(false)

    // Output metadata
    const [lotNumber, setLotNumber] = useState('')
    const [bbDate, setBbDate] = useState('')
    const [actualQuantityProduced, setActualQuantityProduced] = useState('')
    const lotNumberRef = useRef(null)
    const bbDateRef = useRef(null)
    const inputRefs = useRef({})

    // Usage tracking - keyed by allocation _id
    const [usageData, setUsageData] = useState({})
    // { allocation_id: { quantity_used, quantity_returned, return_location_id, quantity_disposed, dispose_reason, dispose_comments } }

    // Expandable rows - tracks which allocation rows show the leftover panel
    const [expandedRows, setExpandedRows] = useState({})

    // Warehouse list
    const [warehouses, setWarehouses] = useState([])

    // Inline return: warehouse selection per allocation, location cache per warehouse
    const [selectedWarehouses, setSelectedWarehouses] = useState({})
    const [locationsByWarehouse, setLocationsByWarehouse] = useState({})

    // Original recipe reference
    const [originalRecipe, setOriginalRecipe] = useState(null)
    const [recipeLoading, setRecipeLoading] = useState(false)
    const [showRecipe, setShowRecipe] = useState(false)

    useEffect(() => {
        fetchProductionLot()
        fetchWarehouses()
    }, [id])

    // Permission check - must be after all hooks
    if (!permissionsLoading && !hasPermission('production.finalize')) {
        return (
            <Container className="py-4">
                <Alert variant="danger">
                    You don&apos;t have permission to finalize production. Required: <code>production.finalize</code>
                </Alert>
                <Button variant="outline-primary" onClick={() => navigate('/productions')}>
                    Back to Production LOTs
                </Button>
            </Container>
        )
    }

    const fetchProductionLot = async () => {
        try {
            setLoading(true)
            const data = await productionAPI.getById(id)

            if (data.status !== 'in_progress') {
                setError('This production LOT cannot be finalized (not in progress)')
                return
            }

            setProductionLot(data)
            setLotNumber(data.lot_number || '')
            setBbDate(data.output_bb_date || '')
            setActualQuantityProduced(data.target_quantity.toString())

            // Initialize usage data with allocated quantities
            const initialUsage = {}
            if (data.lot_ingredients) {
                data.lot_ingredients.forEach(ingredient => {
                    if (ingredient.inventory_allocations) {
                        ingredient.inventory_allocations.forEach(allocation => {
                            const disposed = parseFloat(allocation.quantity_disposed || 0)
                            const returned = parseFloat(allocation.quantity_returned || 0)
                            const available = parseFloat(allocation.quantity_allocated) - disposed - returned
                            initialUsage[allocation._id] = {
                                quantity_used: available.toString(),
                                quantity_returned: '',
                                return_location_id: '',
                                quantity_disposed: '',
                                dispose_reason: '',
                                dispose_comments: '',
                            }
                        })
                    }
                })
            }
            setUsageData(initialUsage)
        } catch (err) {
            setError(formatErrorResponse(err) || 'Failed to load production LOT')
        } finally {
            setLoading(false)
        }
    }

    const fetchWarehouses = async () => {
        try {
            const data = await warehouseAPI.getAll()
            const activeWarehouses = Array.isArray(data)
                ? data.filter(w => w.is_active)
                : (data.results || []).filter(w => w.is_active)
            setWarehouses(activeWarehouses)
        } catch (error) {
            console.error('Error fetching warehouses:', error)
        }
    }

    const fetchOriginalRecipe = async () => {
        if (originalRecipe || recipeLoading) return
        if (!productionLot?.recipe) return
        try {
            setRecipeLoading(true)
            const data = await recipeAPI.getById(productionLot.recipe)
            setOriginalRecipe(data)
        } catch (err) {
            console.error('Error fetching original recipe:', err)
        } finally {
            setRecipeLoading(false)
        }
    }

    const handleToggleRecipe = () => {
        const newShow = !showRecipe
        setShowRecipe(newShow)
        if (newShow && !originalRecipe && !recipeLoading) {
            fetchOriginalRecipe()
        }
    }

    const toggleRow = (allocationId) => {
        setExpandedRows(prev => ({
            ...prev,
            [allocationId]: !prev[allocationId]
        }))
    }

    const handleUsageChange = (allocationId, field, value) => {
        setUsageData(prev => {
            const updated = {
                ...prev[allocationId],
                [field]: value
            }

            // When quantity_used changes, check if remainder drops to 0
            if (field === 'quantity_used') {
                const maxAvailable = findMaxAvailable(allocationId)
                const newUsed = parseFloat(value) || 0
                const newRemainder = parseFloat(Math.max(0, maxAvailable - newUsed).toFixed(4))
                if (newRemainder <= 0) {
                    updated.quantity_returned = ''
                    updated.return_location_id = ''
                    updated.quantity_disposed = ''
                    updated.dispose_reason = ''
                    updated.dispose_comments = ''
                    setExpandedRows(prev2 => ({ ...prev2, [allocationId]: false }))
                    setSelectedWarehouses(prev2 => ({ ...prev2, [allocationId]: '' }))
                } else {
                    // Auto-expand when leftovers exist so user can specify return/dispose
                    setExpandedRows(prev2 => ({ ...prev2, [allocationId]: true }))
                }
            }

            if (field === 'quantity_returned') {
                const maxAvailable = findMaxAvailable(allocationId)
                const qtyUsed = parseFloat(updated.quantity_used) || 0
                const remainder = parseFloat(Math.max(0, maxAvailable - qtyUsed).toFixed(4))
                const newReturned = parseFloat(value) || 0
                const complement = parseFloat(Math.max(0, remainder - newReturned).toFixed(4))
                updated.quantity_disposed = complement > 0 ? complement.toString() : ''
            }

            if (field === 'quantity_disposed') {
                const maxAvailable = findMaxAvailable(allocationId)
                const qtyUsed = parseFloat(updated.quantity_used) || 0
                const remainder = parseFloat(Math.max(0, maxAvailable - qtyUsed).toFixed(4))
                const newDisposed = parseFloat(value) || 0
                const complement = parseFloat(Math.max(0, remainder - newDisposed).toFixed(4))
                updated.quantity_returned = complement > 0 ? complement.toString() : ''
            }

            return { ...prev, [allocationId]: updated }
        })
    }

    const fetchLocationsForWarehouse = async (warehouseId) => {
        if (locationsByWarehouse[warehouseId]) return locationsByWarehouse[warehouseId]
        try {
            const data = await locationAPI.getAll(warehouseId)
            const active = Array.isArray(data)
                ? data.filter(l => l.is_active)
                : (data.results || []).filter(l => l.is_active)
            setLocationsByWarehouse(prev => ({ ...prev, [warehouseId]: active }))
            return active
        } catch (err) {
            console.error('Error fetching locations:', err)
            return []
        }
    }

    const handleWarehouseChange = async (allocationId, warehouseId) => {
        setSelectedWarehouses(prev => ({ ...prev, [allocationId]: warehouseId }))
        handleUsageChange(allocationId, 'return_location_id', '')
        if (warehouseId) {
            await fetchLocationsForWarehouse(warehouseId)
        }
    }

    const calculateTotalCost = () => {
        if (!productionLot) return 0

        let total = 0
        if (productionLot.lot_ingredients) {
            productionLot.lot_ingredients.forEach(ingredient => {
                if (ingredient.inventory_allocations) {
                    ingredient.inventory_allocations.forEach(allocation => {
                        const usage = usageData[allocation._id]
                        if (usage) {
                            const qtyUsed = parseFloat(usage.quantity_used) || 0
                            const costPerUnit = parseFloat(allocation.original_cost) || 0
                            total += qtyUsed * costPerUnit
                        }
                    })
                }
            })
        }
        return total
    }

    const calculateUnitCost = () => {
        const totalCost = calculateTotalCost()
        const qty = parseFloat(actualQuantityProduced) || 0
        if (qty <= 0) return 0
        return totalCost / qty
    }

    const findMaxAvailable = (allocationId) => {
        if (!productionLot?.lot_ingredients) return 0
        for (const ing of productionLot.lot_ingredients) {
            if (ing.inventory_allocations) {
                const alloc = ing.inventory_allocations.find(a => String(a._id) === String(allocationId))
                if (alloc) {
                    const previouslyDisposed = parseFloat(alloc.quantity_disposed || 0)
                    const previouslyReturned = parseFloat(alloc.quantity_returned || 0)
                    return parseFloat(alloc.quantity_allocated) - previouslyDisposed - previouslyReturned
                }
            }
        }
        return 0
    }

    const isAllBalanced = () => {
        if (!productionLot?.lot_ingredients) return false
        for (const ingredient of productionLot.lot_ingredients) {
            if (!ingredient.inventory_allocations) continue
            for (const allocation of ingredient.inventory_allocations) {
                const usage = usageData[allocation._id]
                if (!usage) return false
                const previouslyDisposed = parseFloat(allocation.quantity_disposed || 0)
                const previouslyReturned = parseFloat(allocation.quantity_returned || 0)
                const maxAvailable = parseFloat(allocation.quantity_allocated) - previouslyDisposed - previouslyReturned
                const qtyUsed = parseFloat(usage.quantity_used) || 0
                const qtyReturned = parseFloat(usage.quantity_returned) || 0
                const qtyDisposed = parseFloat(usage.quantity_disposed) || 0
                const total = parseFloat((qtyUsed + qtyReturned + qtyDisposed).toFixed(4))
                if (Math.abs(total - maxAvailable) > 0.00005) return false
            }
        }
        return true
    }

    const allBalanced = isAllBalanced()

    const validateFinalization = () => {
        if (!lotNumber.trim()) {
            setError('Please enter a LOT number')
            lotNumberRef.current?.focus()
            return false
        }
        if (!bbDate) {
            setError('Please enter a Best Before date')
            bbDateRef.current?.focus()
            return false
        }
        if (!actualQuantityProduced || parseFloat(actualQuantityProduced) <= 0) {
            setError('Please enter the actual quantity produced')
            return false
        }

        // Validate usage data
        for (const [allocationId, usage] of Object.entries(usageData)) {
            const qtyUsed = parseFloat(usage.quantity_used) || 0
            const qtyReturned = parseFloat(usage.quantity_returned) || 0
            const qtyDisposed = parseFloat(usage.quantity_disposed) || 0

            const maxAvailable = findMaxAvailable(allocationId)

            if (qtyUsed < 0) {
                setError('Quantities cannot be negative')
                inputRefs.current[`${allocationId}_qty_used`]?.focus()
                return false
            }
            if (qtyUsed > maxAvailable + 0.00005) {
                setError('Quantity used cannot exceed allocated quantity')
                inputRefs.current[`${allocationId}_qty_used`]?.focus()
                return false
            }
            if (qtyReturned < 0 || qtyDisposed < 0) {
                setError('Quantities cannot be negative')
                return false
            }
            const remainder = parseFloat(Math.max(0, maxAvailable - qtyUsed).toFixed(4))

            if (parseFloat((qtyReturned + qtyDisposed).toFixed(4)) > remainder) {
                setError('Return + Dispose cannot exceed the remaining quantity')
                return false
            }

            if (qtyReturned > 0 && !usage.return_location_id) {
                if (!selectedWarehouses[allocationId]) {
                    setError('Please select a return warehouse and location for items being returned')
                    inputRefs.current[`${allocationId}_warehouse`]?.focus()
                } else {
                    setError('Please select a return location for items being returned')
                    inputRefs.current[`${allocationId}_location`]?.focus()
                }
                return false
            }

            if (qtyDisposed > 0 && !usage.dispose_reason) {
                setError('Please select a dispose reason for items being disposed')
                return false
            }
        }

        return true
    }

    const handleFinalize = async () => {
        setError(null)

        if (!validateFinalization()) {
            return
        }

        setFinalizing(true)

        try {
            // Build usage array — all quantities must be explicitly accounted for
            const usage = Object.entries(usageData).map(([allocationId, data]) => {
                const qtyUsed = parseFloat(data.quantity_used) || 0
                const qtyReturned = parseFloat(data.quantity_returned) || 0
                const qtyDisposedByUser = parseFloat(data.quantity_disposed) || 0

                return {
                    production_lot_inventory_item_id: parseInt(allocationId),
                    quantity_used: qtyUsed,
                    quantity_returned: qtyReturned,
                    return_location_id: data.return_location_id ? parseInt(data.return_location_id) : null,
                    quantity_disposed: qtyDisposedByUser,
                    dispose_reason: qtyDisposedByUser > 0 ? data.dispose_reason : '',
                    dispose_comments: data.dispose_comments || '',
                }
            })

            const payload = {
                lot_number: lotNumber.trim(),
                actual_quantity_produced: parseFloat(actualQuantityProduced),
                bb_date: bbDate,
                usage
            }

            await productionAPI.finalize(id, payload)
            navigate(`/production/${id}`)
        } catch (err) {
            setError(formatErrorResponse(err) || 'Failed to finalize production')
        } finally {
            setFinalizing(false)
        }
    }

    if (loading) {
        return (
            <Container className="py-4">
                <div className="text-center">
                    <Spinner animation="border" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </Spinner>
                </div>
            </Container>
        )
    }

    if (!productionLot) {
        return (
            <Container className="py-4">
                <Alert variant="danger">{error || 'Production lot not found'}</Alert>
                <Button variant="outline-primary" onClick={() => navigate('/productions')}>
                    Back to List
                </Button>
            </Container>
        )
    }

    const totalCost = calculateTotalCost()
    const unitCost = calculateUnitCost()

    return (
        <Container className="py-4">
            <Row className="mb-4">
                <Col>
                    <h1>Finalize Production: {productionLot.lot_number}</h1>
                </Col>
            </Row>

            {/* Output Metadata */}
            <Card className="mb-4">
                <Card.Header>
                    <h5 className="mb-0">Output Product Information</h5>
                </Card.Header>
                <Card.Body>
                    <Row>
                        <Col md={4}>
                            <Form.Group className="mb-3">
                                <Form.Label>LOT Number <span className="text-danger">*</span></Form.Label>
                                <Form.Control
                                    ref={lotNumberRef}
                                    type="text"
                                    value={lotNumber}
                                    onChange={(e) => setLotNumber(e.target.value)}
                                    autoComplete="off"
                                />
                            </Form.Group>
                        </Col>
                        <Col md={4}>
                            <Form.Group className="mb-3">
                                <Form.Label>Best Before Date <span className="text-danger">*</span></Form.Label>
                                <Form.Control
                                    ref={bbDateRef}
                                    type="date"
                                    value={bbDate}
                                    onChange={(e) => setBbDate(e.target.value)}
                                />
                            </Form.Group>
                        </Col>
                        <Col md={4}>
                            <Form.Group className="mb-3">
                                <Form.Label>Actual Quantity Produced <span className="text-danger">*</span></Form.Label>
                                <QuantityPicker
                                    casePack={productionLot.output_stock_item_case_pack || 1}
                                    uom={productionLot.output_stock_item_uom || ''}
                                    value={actualQuantityProduced}
                                    onChange={(qty) => setActualQuantityProduced(qty)}
                                    required
                                />
                                <Form.Text className="text-muted">
                                    Target was: {productionLot.target_quantity}
                                </Form.Text>
                            </Form.Group>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            {/* Original Recipe Reference */}
            <Card className="mb-4">
                <Card.Header
                    className="d-flex align-items-center"
                    style={{ cursor: 'pointer' }}
                    onClick={handleToggleRecipe}
                >
                    <button className={`expand-toggle${showRecipe ? ' open' : ''}`}>
                        &#9654;
                    </button>
                    <h5 className="mb-0 ms-2">
                        Original Recipe: {productionLot.recipe_name}
                    </h5>
                    <Link
                        to={`/recipe/${productionLot.recipe}`}
                        target="_blank"
                        className="ms-auto text-decoration-none"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <small>Open Recipe <i className="fas fa-external-link-alt"></i></small>
                    </Link>
                </Card.Header>
                {showRecipe && (
                    <Card.Body>
                        {recipeLoading ? (
                            <div className="text-center py-3">
                                <Spinner animation="border" size="sm" />
                            </div>
                        ) : originalRecipe?.ingredients?.length > 0 ? (
                            <Table striped bordered hover size="sm">
                                <thead>
                                    <tr>
                                        <th style={{ width: '60px' }}>Image</th>
                                        <th>GTIN</th>
                                        <th>Ingredient</th>
                                        <th style={{ minWidth: '280px' }}>Quantity</th>
                                        <th>Waste %</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {originalRecipe.ingredients.map((ingredient) => (
                                        <tr key={ingredient._id}>
                                            <td>
                                                {ingredient.stock_item_image ? (() => {
                                                    const stockItemData = ingredient.stock_item_data
                                                    const img = (
                                                        <img
                                                            src={ingredient.stock_item_image}
                                                            alt="Ingredient Thumbnail"
                                                            style={{ width: '40px', height: '40px', objectFit: 'contain' }}
                                                        />
                                                    )
                                                    return stockItemData ? (
                                                        <OverlayTrigger
                                                            placement="right"
                                                            overlay={
                                                                <Tooltip className="stock-item-tooltip">
                                                                    <StockItemInfo stockItem={stockItemData} />
                                                                </Tooltip>
                                                            }
                                                        >
                                                            {img}
                                                        </OverlayTrigger>
                                                    ) : img
                                                })() : null}
                                            </td>
                                            <td>
                                                {ingredient.item_family ? '' : <CopyableText value={ingredient.gtin} toastLabel="GTIN" />}
                                            </td>
                                            <td>
                                                {ingredient.stock_item ? (
                                                    <Link to={`/stock-item/${ingredient.stock_item}`} target="_blank">
                                                        {ingredient.ingredient_name || ingredient.stock_item_name}
                                                    </Link>
                                                ) : (ingredient.ingredient_name || ingredient.item_family_name)}
                                            </td>
                                            <td>
                                                {ingredient.input_mode === 'weight' && ingredient.weight_value ? (
                                                    <span>{ingredient.weight_value} {ingredient.weight_unit}</span>
                                                ) : (
                                                    <QuantityDisplay
                                                        casePack={ingredient.case_pack || 1}
                                                        uom={ingredient.unit_of_measure || ''}
                                                        value={ingredient.quantity}
                                                    />
                                                )}
                                            </td>
                                            <td>
                                                <Badge bg={parseFloat(ingredient.waste_percentage) < 0 ? 'warning' : 'secondary'} className="bg-opacity-25 text-body rounded-pill">
                                                    {ingredient.waste_percentage}%
                                                </Badge>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        ) : (
                            <p className="text-muted mb-0">No ingredients found in recipe.</p>
                        )}
                    </Card.Body>
                )}
            </Card>

            {/* Ingredient Usage */}
            <Card className="mb-4">
                <Card.Header>
                    <h5 className="mb-0">Ingredient Usage</h5>
                </Card.Header>
                <Card.Body>
                    {productionLot.lot_ingredients && productionLot.lot_ingredients.map((lotIngredient) => (
                        <Card key={lotIngredient._id} className="mb-3">
                            <Card.Header className="d-flex align-items-center">
                                <span style={{ flexShrink: 0 }}>
                                    {lotIngredient.recipe_ingredient_data?.item_family
                                        ? ''
                                        : <CopyableText value={lotIngredient.recipe_ingredient_data?.gtin} toastLabel="GTIN" />}
                                </span>
                                <strong>{lotIngredient.ingredient_name}</strong>
                                <span className="text-muted ms-2">
                                    {(() => {
                                        const ri = lotIngredient.recipe_ingredient_data
                                        const isWeight = ri?.input_mode === 'weight'
                                        const cp = ri?.case_pack || 1
                                        const u = ri?.unit_of_measure || ''
                                        return (
                                            <>
                                                (Required: {isWeight ? (
                                                    <span>{lotIngredient.required_quantity} {ri?.weight_unit || 'lbs'}
                                                        {ri?.weight_value && ri?.quantity
                                                            ? <span> (<QuantityDisplay casePack={cp} uom={u} value={weightToCases(lotIngredient.required_quantity, ri.weight_value, ri.quantity)} />)</span>
                                                            : null}
                                                    </span>
                                                ) : (
                                                    <QuantityDisplay casePack={cp} uom={u} value={lotIngredient.required_quantity} />
                                                )}, Picked: <QuantityDisplay casePack={cp} uom={u} value={lotIngredient.picked_quantity} />)
                                            </>
                                        )
                                    })()}
                                </span>
                            </Card.Header>
                            <Card.Body className="p-0">
                                <Table className="mb-0">
                                    <thead>
                                        <tr>
                                            <th style={{ width: '40px' }}></th>
                                            <th>Item</th>
                                            <th>LOT</th>
                                            <th>Allocated</th>
                                            <th>Cost/Unit</th>
                                            <th>Qty Used</th>
                                            <th>Leftovers</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {lotIngredient.inventory_allocations && lotIngredient.inventory_allocations.map((allocation) => {
                                            const usage = usageData[allocation._id] || {}
                                            const previouslyDisposed = parseFloat(allocation.quantity_disposed || 0)
                                            const previouslyReturned = parseFloat(allocation.quantity_returned || 0)
                                            const maxAvailable = parseFloat(allocation.quantity_allocated) - previouslyDisposed - previouslyReturned
                                            const qtyUsed = parseFloat(usage.quantity_used) || 0
                                            const qtyReturned = parseFloat(usage.quantity_returned) || 0
                                            const qtyDisposed = parseFloat(usage.quantity_disposed) || 0
                                            const remainder = parseFloat(Math.max(0, maxAvailable - qtyUsed).toFixed(4))
                                            const isExpanded = expandedRows[allocation._id]
                                            const warehouseId = selectedWarehouses[allocation._id] || ''
                                            const locations = warehouseId ? (locationsByWarehouse[warehouseId] || []) : []
                                            const casePack = allocation.stock_item_case_pack || 1
                                            const uom = allocation.stock_item_uom || ''

                                            // Leftover accounting
                                            const accounted = parseFloat((qtyReturned + qtyDisposed).toFixed(4))
                                            const gap = parseFloat(Math.max(0, remainder - accounted).toFixed(4))
                                            const isBalanced = remainder > 0 && accounted > 0 && gap === 0
                                            const isOver = accounted > remainder

                                            // Reason options for react-select (exclude empty placeholder)
                                            const disposeReasonOpts = reasonOptions
                                                .filter(r => r.value)
                                                .map(r => ({ value: r.value, label: r.label }))

                                            return (
                                                <React.Fragment key={allocation._id}>
                                                    <tr>
                                                        <td className="text-center" style={{ verticalAlign: 'middle' }}>
                                                            {remainder > 0 ? (
                                                                <button
                                                                    className={`expand-toggle${isExpanded ? ' open' : ''}`}
                                                                    onClick={() => toggleRow(allocation._id)}
                                                                    title="Manage leftovers"
                                                                >
                                                                    &#9654;
                                                                </button>
                                                            ) : null}
                                                        </td>
                                                        <td>
                                                            {allocation.stock_item_name || '-'}
                                                            {previouslyReturned > 0 && (
                                                                <small className="text-muted d-block">
                                                                    Previously returned: <QuantityDisplay casePack={casePack} uom={uom} value={previouslyReturned} />
                                                                </small>
                                                            )}
                                                            {previouslyDisposed > 0 && (
                                                                <small className="text-muted d-block">
                                                                    Previously disposed: <QuantityDisplay casePack={casePack} uom={uom} value={previouslyDisposed} />
                                                                </small>
                                                            )}
                                                        </td>
                                                        <td>{allocation.original_lot || '-'}</td>
                                                        <td><QuantityDisplay casePack={casePack} uom={uom} value={maxAvailable} /></td>
                                                        <td>{formatUnitCost(allocation.original_cost)}</td>
                                                        <td>
                                                            <QuantityPicker
                                                                casePack={casePack}
                                                                uom={uom}
                                                                value={usage.quantity_used ?? ''}
                                                                onChange={(val) => handleUsageChange(
                                                                    allocation._id,
                                                                    'quantity_used',
                                                                    val
                                                                )}
                                                                max={maxAvailable}
                                                            />
                                                        </td>
                                                        <td style={{ verticalAlign: 'middle' }}>
                                                            {remainder > 0 ? (
                                                                <span className="leftover-badge has-leftovers">
                                                                    <QuantityDisplay casePack={casePack} uom={uom} value={remainder} /> remaining
                                                                </span>
                                                            ) : (
                                                                <span className="leftover-badge no-leftovers">No leftovers</span>
                                                            )}
                                                        </td>
                                                    </tr>
                                                    {isExpanded && remainder > 0 && (
                                                        <tr className="expand-panel">
                                                            <td colSpan={7}>
                                                                <Row>
                                                                    {/* Return Section */}
                                                                    <Col md={6}>
                                                                        <div className="action-section">
                                                                            <h6>Return to Inventory</h6>
                                                                            <Form.Group className="mb-2">
                                                                                <Form.Label className="mb-1">Quantity</Form.Label>
                                                                                <QuantityPicker
                                                                                    casePack={casePack}
                                                                                    uom={uom}
                                                                                    value={usage.quantity_returned ?? ''}
                                                                                    onChange={(val) => handleUsageChange(
                                                                                        allocation._id,
                                                                                        'quantity_returned',
                                                                                        val
                                                                                    )}
                                                                                    max={remainder}
                                                                                />
                                                                            </Form.Group>
                                                                            <Form.Group className="mb-2">
                                                                                <Form.Label className="mb-1">Warehouse</Form.Label>
                                                                                <Select
                                                                                    ref={(el) => { inputRefs.current[`${allocation._id}_warehouse`] = el }}
                                                                                    value={warehouses
                                                                                        .map(wh => ({ value: wh._id, label: wh.name }))
                                                                                        .find(opt => String(opt.value) === String(warehouseId)) || null}
                                                                                    onChange={(opt) => handleWarehouseChange(allocation._id, opt?.value || '')}
                                                                                    options={warehouses.map(wh => ({ value: wh._id, label: wh.name }))}
                                                                                    placeholder="Select Warehouse..."
                                                                                    className="react-select-container"
                                                                                    classNamePrefix="react-select"
                                                                                    isClearable
                                                                                />
                                                                            </Form.Group>
                                                                            <Form.Group className="mb-0">
                                                                                <Form.Label className="mb-1">Location</Form.Label>
                                                                                <Select
                                                                                    ref={(el) => { inputRefs.current[`${allocation._id}_location`] = el }}
                                                                                    value={locations
                                                                                        .map(loc => ({ value: loc._id, label: loc.name }))
                                                                                        .find(opt => String(opt.value) === String(usage.return_location_id)) || null}
                                                                                    onChange={(opt) => handleUsageChange(
                                                                                        allocation._id,
                                                                                        'return_location_id',
                                                                                        opt?.value || ''
                                                                                    )}
                                                                                    options={locations.map(loc => ({ value: loc._id, label: loc.name }))}
                                                                                    placeholder="Select Location..."
                                                                                    className="react-select-container"
                                                                                    classNamePrefix="react-select"
                                                                                    isDisabled={!warehouseId}
                                                                                    isClearable
                                                                                />
                                                                            </Form.Group>
                                                                        </div>
                                                                    </Col>
                                                                    {/* Dispose Section */}
                                                                    <Col md={6}>
                                                                        <div className="action-section">
                                                                            <h6>Dispose</h6>
                                                                            <Form.Group className="mb-2">
                                                                                <Form.Label className="mb-1">Quantity</Form.Label>
                                                                                <QuantityPicker
                                                                                    casePack={casePack}
                                                                                    uom={uom}
                                                                                    value={usage.quantity_disposed ?? ''}
                                                                                    onChange={(val) => handleUsageChange(
                                                                                        allocation._id,
                                                                                        'quantity_disposed',
                                                                                        val
                                                                                    )}
                                                                                    max={remainder}
                                                                                />
                                                                            </Form.Group>
                                                                            <Form.Group className="mb-2">
                                                                                <Form.Label className="mb-1">Reason</Form.Label>
                                                                                <Select
                                                                                    value={disposeReasonOpts.find(opt => opt.value === usage.dispose_reason) || null}
                                                                                    onChange={(opt) => handleUsageChange(
                                                                                        allocation._id,
                                                                                        'dispose_reason',
                                                                                        opt?.value || ''
                                                                                    )}
                                                                                    options={disposeReasonOpts}
                                                                                    placeholder="Select Reason..."
                                                                                    className="react-select-container"
                                                                                    classNamePrefix="react-select"
                                                                                    isClearable
                                                                                />
                                                                            </Form.Group>
                                                                            <Form.Group className="mb-0">
                                                                                <Form.Label className="mb-1">Comments</Form.Label>
                                                                                <Form.Control
                                                                                    as="textarea"
                                                                                    rows={2}
                                                                                    value={usage.dispose_comments || ''}
                                                                                    onChange={(e) => handleUsageChange(
                                                                                        allocation._id,
                                                                                        'dispose_comments',
                                                                                        e.target.value
                                                                                    )}
                                                                                    placeholder="Optional comments..."
                                                                                />
                                                                            </Form.Group>
                                                                        </div>
                                                                    </Col>
                                                                </Row>
                                                                {/* Validation summary */}
                                                                <div className={`leftover-summary mt-2 ${isBalanced ? 'balanced' : 'over'}`}>
                                                                    {isOver ? (
                                                                        <>Return (<QuantityDisplay casePack={casePack} uom={uom} value={qtyReturned} />) + Dispose (<QuantityDisplay casePack={casePack} uom={uom} value={qtyDisposed} />) exceeds <QuantityDisplay casePack={casePack} uom={uom} value={remainder} /> remaining</>
                                                                    ) : isBalanced ? (
                                                                        <>
                                                                            {qtyReturned > 0 && <>Return (<QuantityDisplay casePack={casePack} uom={uom} value={qtyReturned} />)</>}
                                                                            {qtyReturned > 0 && qtyDisposed > 0 && ' + '}
                                                                            {qtyDisposed > 0 && <>Dispose (<QuantityDisplay casePack={casePack} uom={uom} value={qtyDisposed} />)</>}
                                                                            {' = '}<QuantityDisplay casePack={casePack} uom={uom} value={remainder} /> remaining &#10003;
                                                                        </>
                                                                    ) : (
                                                                        <><QuantityDisplay casePack={casePack} uom={uom} value={gap} /> remaining not accounted</>
                                                                    )}
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    )}
                                                </React.Fragment>
                                            )
                                        })}
                                    </tbody>
                                </Table>
                            </Card.Body>
                        </Card>
                    ))}
                </Card.Body>
            </Card>

            {/* Cost Summary */}
            <Card className="mb-4">
                <Card.Header>
                    <h5 className="mb-0">Cost Summary</h5>
                </Card.Header>
                <Card.Body>
                    <Row>
                        <Col md={4}>
                            <p><strong>Total Cost:</strong> ${totalCost.toFixed(2)}</p>
                        </Col>
                        <Col md={4}>
                            <p><strong>Unit Cost:</strong> {formatUnitCost(unitCost)}</p>
                        </Col>
                        <Col md={4}>
                            <p><strong>Quantity:</strong> {actualQuantityProduced || 0}</p>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            {error && (
                <Alert variant="danger" dismissible onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}

            {/* Action Buttons */}
            <div className="d-flex gap-2">
                <Button
                    variant="success"
                    onClick={handleFinalize}
                    disabled={finalizing || !allBalanced}
                >
                    {finalizing ? (
                        <>
                            <Spinner animation="border" size="sm" className="me-2" />
                            Finalizing...
                        </>
                    ) : (
                        'Complete Production'
                    )}
                </Button>
                <Button
                    variant="outline-primary"
                    onClick={() => navigate(`/production/${id}`)}
                    disabled={finalizing}
                >
                    Cancel
                </Button>
            </div>
            {!allBalanced && (
                <div className="text-danger mt-2">
                    <small>All allocated quantities must be fully accounted for (used + returned + disposed = allocated)</small>
                </div>
            )}

        </Container>
    )
}

export default ProductionFinalizeScreen
